/*
 * Shrine on Horizon — scroll-snap sliders.
 * slider-component, sh-slideshow-component, shrine-timeline and timeline-slider.
 * Safe to include more than once.
 */
(() => {
  if (customElements.get('slider-component')) return;

  class SliderComponent extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('[id^="Slider-"]');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.enableSliderLooping = false;
      this.currentPageElement = this.querySelector('.slider-counter--current');
      this.pageTotalElement = this.querySelector('.slider-counter--total');
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      this.hasDots = false;
      this.scrollMultiplier = isRtl ? -1 : 1;
      this.verticalDesktop = this.dataset.desktopVertical === 'true';
      this.verticalMobile = this.dataset.mobileVertical === 'true';
      this.vertical = false;

      if (!this.slider || !this.nextButton) return;

      this.slider.addEventListener('scroll', this.update.bind(this));
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

      this.sliderControlWrapper = this.querySelector('.slider-buttons');
      if (this.sliderControlWrapper && this.sliderControlWrapper.querySelector('.slider-counter__link')) {
        this.internalVideos = this.querySelectorAll('internal-video');
        this.pauseVideos = this.dataset.pauseVideos === 'true' && this.internalVideos.length > 0;
        this.sliderFirstItemNode = this.slider.querySelector('.slider__slide');
        this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
        this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
        this.hasDots = true;
      }

      this.initPages();
      new ResizeObserver(() => this.initPages()).observe(this.slider);
    }

    linkToSlide(event) {
      event.preventDefault();
      const linkIndex = this.sliderControlLinksArray.indexOf(event.currentTarget);
      let hiddenBefore = 0;
      for (let i = 0; i < linkIndex; i++) {
        if (this.sliderControlLinksArray[i].classList.contains('hidden')) hiddenBefore++;
      }
      const steps = linkIndex - hiddenBefore + 1 - this.currentPage;
      const scrollPosition = this.vertical ? this.slider.scrollTop : this.slider.scrollLeft * this.scrollMultiplier;
      const itemSize = this.vertical ? this.sliderFirstItemNode.clientHeight : this.sliderFirstItemNode.clientWidth;
      const target = scrollPosition + itemSize * steps;
      if (this.vertical) {
        this.slider.scrollTo({ top: target, behavior: 'smooth' });
      } else {
        this.slider.scrollTo({ left: target * this.scrollMultiplier, behavior: 'smooth' });
      }
    }

    initPages() {
      this.vertical = window.innerWidth < 750 ? this.verticalMobile : this.verticalDesktop;
      this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;

      if (this.vertical) {
        const start = this.sliderItemsToShow[0].offsetTop;
        this.sliderItemOffset = this.sliderItemsToShow[1].offsetTop - start;
        this.slidesPerPage = Math.floor((this.slider.clientHeight - start) / this.sliderItemOffset);
      } else {
        const start = this.sliderItemsToShow[0].offsetLeft * this.scrollMultiplier;
        this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft * this.scrollMultiplier - start;
        this.slidesPerPage = Math.floor((this.slider.clientWidth - start) / this.sliderItemOffset);
      }
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
      this.update();
    }

    resetPages() {
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.initPages();
    }

    update() {
      if (!this.slider || !this.nextButton || !this.sliderItemOffset) return;
      const previousPage = this.currentPage;
      this.currentPage = this.vertical
        ? Math.round(this.slider.scrollTop / this.sliderItemOffset) + 1
        : Math.round((this.slider.scrollLeft * this.scrollMultiplier) / this.sliderItemOffset) + 1;

      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }

      if (this.currentPage != previousPage) {
        this.dispatchEvent(
          new CustomEvent('slideChanged', {
            detail: { currentPage: this.currentPage, currentElement: this.sliderItemsToShow[this.currentPage - 1] },
          })
        );
      }

      if (this.hasDots) {
        let visibleIndex = 0;
        const activeIndex = this.currentPage - 1;
        this.sliderControlLinksArray.forEach((link) => {
          link.classList.remove('slider-counter__link--active');
          link.removeAttribute('aria-current');
          if (link.classList.contains('hidden')) return;
          if (visibleIndex === activeIndex) {
            link.classList.add('slider-counter__link--active');
            link.setAttribute('aria-current', 'true');
          }
          visibleIndex++;
        });
      }

      const activeSlide = this.sliderItems[this.currentPage - 1];
      if (this.pauseVideos && activeSlide) {
        this.internalVideos.forEach((video) => {
          if (activeSlide.id == video.closest('[id^="Slide-"]').id) return;
          if (video.dataset.autoplay === 'true') {
            video.querySelector('video').muted = true;
            video.classList.add('internal-video--muted');
          } else {
            video.querySelector('video').pause();
            video.classList.remove('internal-video--playing');
          }
        });
      }

      if (this.enableSliderLooping) return;
      const last = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
      if (this.vertical) {
        this.toggleDisabled(this.prevButton, this.slider.scrollTop === 0);
      } else {
        this.toggleDisabled(this.prevButton, this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0);
      }
      this.toggleDisabled(this.nextButton, this.isSlideVisible(last, true));
    }

    toggleDisabled(button, disabled) {
      disabled ? button.setAttribute('disabled', 'disabled') : button.removeAttribute('disabled');
    }

    isSlideVisible(element, useTolerance = false, offset = 0, checkStart = false) {
      const tolerance = useTolerance ? 10 : 0;
      if (this.vertical) {
        const end = element.offsetTop + element.clientHeight;
        const start = element.offsetTop;
        const viewStart = this.slider.scrollTop - tolerance + offset;
        const viewEnd = this.slider.scrollTop + this.slider.clientHeight + tolerance - offset;
        return end <= viewEnd && (!checkStart || start >= viewStart);
      }
      const end = element.offsetLeft * this.scrollMultiplier + element.clientWidth;
      const start = element.offsetLeft * this.scrollMultiplier;
      const viewStart = this.slider.scrollLeft * this.scrollMultiplier - tolerance + offset;
      const viewEnd = this.slider.scrollLeft * this.scrollMultiplier + this.slider.clientWidth + tolerance - offset;
      return end <= viewEnd && (!checkStart || start >= viewStart);
    }

    onButtonClick(event) {
      event.preventDefault();
      const step = event.currentTarget.dataset.step || 1;
      const forward = event.currentTarget.name === 'next';
      if (this.vertical) {
        const position = this.slider.scrollTop;
        const target = forward ? position + step * this.sliderItemOffset : position - step * this.sliderItemOffset;
        this.slider.scrollTo({ top: target, behavior: 'smooth' });
      } else {
        const position = this.slider.scrollLeft * this.scrollMultiplier;
        this.slideScrollPosition = forward ? position + step * this.sliderItemOffset : position - step * this.sliderItemOffset;
        this.slider.scrollTo({ left: this.slideScrollPosition * this.scrollMultiplier, behavior: 'smooth' });
      }
    }
  }
  customElements.define('slider-component', SliderComponent);
  window.SliderComponent = SliderComponent;

  class SlideshowComponent extends SliderComponent {
    constructor() {
      super();
      this.sliderControlWrapper = this.querySelector('.slider-buttons');
      this.enableSliderLooping = true;
      if (!this.sliderControlWrapper || !this.slider) return;

      this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
      if (this.sliderItemsToShow?.length > 0) this.currentPage = 1;

      this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
      this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
      this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
      this.setSlideVisibility();

      if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
    }

    setAutoPlay() {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.autoplaySpeed = this.slider.dataset.speed * 1000;
      this.sliderAutoplayButton?.addEventListener('click', this.autoPlayToggle.bind(this));
      this.addEventListener('mouseover', this.focusInHandling.bind(this));
      this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
      this.addEventListener('focusin', this.focusInHandling.bind(this));
      this.addEventListener('focusout', this.focusOutHandling.bind(this));
      this.play();
      this.autoplayButtonIsSetToPlay = true;
    }

    onButtonClick(event) {
      super.onButtonClick(event);
      const isFirstSlide = this.currentPage === 1;
      const isLastSlide = this.currentPage === this.sliderItemsToShow.length;
      if (!isFirstSlide && !isLastSlide) return;

      if (isFirstSlide && event.currentTarget.name === 'previous') {
        this.slideScrollPosition =
          this.slider.scrollLeft * this.scrollMultiplier + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
      } else if (isLastSlide && event.currentTarget.name === 'next') {
        this.slideScrollPosition = 0;
      }
      this.slider.scrollTo({ left: this.slideScrollPosition * this.scrollMultiplier });
    }

    update() {
      super.update();
      if (!this.prevButton) return;
      this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
      this.prevButton.removeAttribute('disabled');
      if (!this.sliderControlButtons.length) return;
      this.sliderControlButtons.forEach((link) => {
        link.classList.remove('slider-counter__link--active');
        link.removeAttribute('aria-current');
      });
      const active = this.sliderControlButtons[this.currentPage - 1];
      if (active) {
        active.classList.add('slider-counter__link--active');
        active.setAttribute('aria-current', true);
      }
    }

    autoPlayToggle() {
      this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
      this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
      this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
    }

    isAutoplayButton(target) {
      return this.sliderAutoplayButton && (target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(target));
    }

    focusOutHandling(event) {
      if (this.autoplayButtonIsSetToPlay && !this.isAutoplayButton(event.target)) this.play();
    }

    focusInHandling(event) {
      if (this.isAutoplayButton(event.target) && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    }

    play() {
      this.slider.setAttribute('aria-live', 'off');
      clearInterval(this.autoplay);
      this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
    }

    pause() {
      this.slider.setAttribute('aria-live', 'polite');
      clearInterval(this.autoplay);
    }

    togglePlayButtonState(pauseAutoplay) {
      if (!this.sliderAutoplayButton) return;
      this.sliderAutoplayButton.classList.toggle('slideshow__autoplay--paused', pauseAutoplay);
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        pauseAutoplay ? window.accessibilityStrings.playSlideshow : window.accessibilityStrings.pauseSlideshow
      );
    }

    autoRotateSlides() {
      const slideScrollPosition =
        this.currentPage === this.sliderItems.length
          ? 0
          : this.slider.scrollLeft * this.scrollMultiplier + this.slider.querySelector('.slideshow__slide').clientWidth;
      this.slider.scrollTo({ left: slideScrollPosition * this.scrollMultiplier });
    }

    setSlideVisibility() {
      (this.sliderItemsToShow || []).forEach((item, index) => {
        const links = item.querySelectorAll('a');
        const active = index === this.currentPage - 1;
        links.forEach((link) => (active ? link.removeAttribute('tabindex') : link.setAttribute('tabindex', '-1')));
        item.setAttribute('aria-hidden', active ? 'false' : 'true');
        active ? item.removeAttribute('tabindex') : item.setAttribute('tabindex', '-1');
      });
    }

    linkToSlide(event) {
      event.preventDefault();
      const position = this.slider.scrollLeft * this.scrollMultiplier;
      const target =
        position +
        this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
      this.slider.scrollTo({ left: target * this.scrollMultiplier });
    }
  }
  customElements.define('sh-slideshow-component', SlideshowComponent);

  /* ---------- Timeline ---------- */

  class ShrineTimeline extends HTMLElement {
    connectedCallback() {
      this.direction = this.dataset.direction || 'vertical';
      this.styleVertical = this.dataset.styleVertical || 'classic';
      this.styleHorizontal = this.dataset.styleHorizontal || 'classic';
      this.animateVertical = this.dataset.animateVertical === 'true';
      this.animateHorizontal = this.dataset.animateHorizontal === 'true';
      this.pointAnimMs = Math.max(0, parseFloat(this.dataset.pointAnimationDuration || '0')) * 1000;
      this.lineAnimMs = Math.max(0, parseFloat(this.dataset.lineAnimationDuration || '0')) * 1000;
      this.stepDelayMs = this.pointAnimMs + this.lineAnimMs;
      this.timelineRoot = this.querySelector('.timeline') || this;
      this.itemsContainer = this.querySelector('.timeline__items');
      this.items = Array.from(this.querySelectorAll('.timeline__item'));
      this.activeContentOutlet = this.querySelector('[data-active-outlet]');
      this.sliderComponent = this.querySelector('timeline-slider');
      if (!this.itemsContainer || this.items.length === 0) return;

      this.items.forEach((item, index) => (item.dataset.index = String(index)));
      this.activeIndex = 0;
      this.dataset.mode = this.direction === 'vertical' ? this.styleVertical : this.styleHorizontal;
      this._suppressScroll = false;
      this._restoreTimer = null;

      if (this.direction === 'vertical') {
        this.alignMarkersToHeaders();
        window.addEventListener('resize', debounce(() => this.alignMarkersToHeaders(), 200));
        this.initVertical();
      } else if (this.direction === 'horizontal') {
        this.initHorizontal();
      }
    }

    initVertical() {
      if (this.styleVertical === 'classic') this.initClassicMode(this.animateVertical);
      if (this.styleVertical === 'scroll_activated') this.initVerticalScrollActivated();
      if (this.styleVertical === 'accordion') this.initVerticalAccordion();
    }

    initHorizontal() {
      if (this.styleHorizontal === 'classic') this.initClassicMode(this.animateHorizontal);
      if (this.styleHorizontal === 'slider_click') this.initHorizontalSliderClick();
    }

    setActiveUpTo(index) {
      this.items.forEach((item, i) => item.classList.toggle('is-active', i <= index));
      this.activeIndex = index;
    }

    setOnlyOpen(index) {
      this.items.forEach((item, i) => item.classList.toggle('is-open', i === index));
    }

    updateActiveContentFrom(index) {
      const outlet = this.activeContentOutlet;
      const item = this.items[index];
      if (!outlet || !item) return;
      const source = item.querySelector('.timeline-item__active');
      if (!source) return;

      let inner = outlet.querySelector('.timeline-active-content__inner');
      if (!inner) {
        inner = document.createElement('div');
        inner.className = 'timeline-active-content__inner';
        inner.innerHTML = outlet.innerHTML;
        outlet.innerHTML = '';
        outlet.appendChild(inner);
      }

      const startHeight = outlet.offsetHeight;
      outlet.style.height = `${startHeight}px`;
      const transition = outlet.style.transition;
      outlet.style.transition = 'none';
      outlet.style.opacity = '0';
      void outlet.offsetHeight;
      inner.innerHTML = source.innerHTML;
      outlet.style.height = 'auto';
      const endHeight = outlet.offsetHeight;
      outlet.style.height = `${startHeight}px`;
      void outlet.offsetHeight;
      outlet.style.transition = transition || '';
      requestAnimationFrame(() => {
        outlet.style.height = `${endHeight}px`;
        outlet.style.opacity = '1';
      });
      const onEnd = (event) => {
        if (event.propertyName !== 'height') return;
        outlet.style.height = '';
        outlet.removeEventListener('transitionend', onEnd);
      };
      outlet.addEventListener('transitionend', onEnd);
    }

    whenSectionEntersViewport(callback) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          callback();
        },
        { root: null, threshold: 0.15 }
      );
      observer.observe(this);
    }

    computeIndexClosestToViewportCenter() {
      const center = window.innerHeight / 2;
      let closest = 0;
      let distance = Infinity;
      this.items.forEach((item, i) => {
        const rect = item.getBoundingClientRect();
        const d = Math.abs(rect.top + rect.height / 2 - center);
        if (d < distance) {
          distance = d;
          closest = i;
        }
      });
      return closest;
    }

    attachScrollListener(callback) {
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          callback();
          ticking = false;
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }

    withCompressedDurationsToFinishTogether(nextIndex, previousIndex, apply) {
      const factor = Math.max(1, Math.abs(Number(nextIndex) - Number(previousIndex)));
      if (this._restoreTimer) {
        clearTimeout(this._restoreTimer);
        this._restoreTimer = null;
      }
      if (!this.timelineRoot || factor <= 1) {
        apply?.();
        return 0;
      }
      const root = this.timelineRoot;
      const previousPoint = root.style.getPropertyValue('--point-animation-duration');
      const previousLine = root.style.getPropertyValue('--line-animation-duration');
      root.style.setProperty('--point-animation-duration', `${this.pointAnimMs / factor / 1000}s`);
      root.style.setProperty('--line-animation-duration', `${this.lineAnimMs / factor / 1000}s`);
      apply?.();
      const total = this.pointAnimMs + this.lineAnimMs;
      this._restoreTimer = window.setTimeout(() => {
        previousPoint
          ? root.style.setProperty('--point-animation-duration', previousPoint.trim())
          : root.style.removeProperty('--point-animation-duration');
        previousLine
          ? root.style.setProperty('--line-animation-duration', previousLine.trim())
          : root.style.removeProperty('--line-animation-duration');
        this._restoreTimer = null;
      }, total);
      return total;
    }

    smoothScrollItemToViewportCenter(index) {
      const item = this.items[index];
      if (!item) return 0;
      const rect = item.getBoundingClientRect();
      const delta = rect.top + rect.height / 2 - window.innerHeight / 2;
      if (Math.abs(delta) < 2) return 0;
      window.scrollTo({ top: window.scrollY + delta, behavior: 'smooth' });
      return Math.min(900, Math.max(200, Math.abs(delta) / 1.2));
    }

    activateItem(index, { scrollIntoView = false } = {}) {
      if (scrollIntoView) this._suppressScroll = true;
      const animationTime = this.withCompressedDurationsToFinishTogether(index, this.activeIndex ?? 0, () => {
        this.setOnlyOpen(index);
        this.setActiveUpTo(index);
        this.updateActiveContentFrom(index);
      });
      if (!scrollIntoView) return;
      const scrollTime = this.smoothScrollItemToViewportCenter(index);
      window.setTimeout(() => (this._suppressScroll = false), Math.max(animationTime, scrollTime) + 50);
    }

    attachItemClickActivation(scrollIntoView) {
      this.items.forEach((item, index) => {
        item.addEventListener('click', (event) => {
          if (event.target.closest('a, input, select, textarea, [data-stop-activation]')) return;
          this.activateItem(index, { scrollIntoView });
          this.sliderComponent?.setCurrentIndexFromTimeline(index);
        });
      });
    }

    initClassicMode(animate) {
      if (!animate) return;
      this.whenSectionEntersViewport(() => {
        this.items.forEach((item, index) => setTimeout(() => item.classList.add('is-active'), this.stepDelayMs * index));
      });
    }

    initVerticalScrollActivated() {
      this.whenSectionEntersViewport(() => {
        this.setActiveUpTo(0);
        this.setOnlyOpen(0);
        this.updateActiveContentFrom(0);
        const sync = () => {
          if (this._suppressScroll) return;
          const index = this.computeIndexClosestToViewportCenter();
          if (index === this.activeIndex) return;
          this.setActiveUpTo(index);
          this.setOnlyOpen(index);
          this.updateActiveContentFrom(index);
        };
        sync();
        this.detachScroll = this.attachScrollListener(sync);
        this.attachItemClickActivation(true);
      });
    }

    initVerticalAccordion() {
      this.setActiveUpTo(0);
      this.setOnlyOpen(0);
      this.updateActiveContentFrom(0);
      this.items.forEach((item, index) => {
        const header = item.querySelector('.timeline-item__header');
        header?.addEventListener('click', () => {
          if (header.getAttribute('aria-disabled') !== 'true') this.activateItem(index, { scrollIntoView: false });
        });
      });
    }

    initHorizontalSliderClick() {
      this.setActiveUpTo(0);
      this.setOnlyOpen(0);
      this.updateActiveContentFrom(0);
      this.attachItemClickActivation(false);
      this.sliderComponent?.addEventListener('slideChanged', (event) => {
        const page = Number(event.detail?.currentPage);
        if (!page || Number.isNaN(page)) return;
        const index = page - 1;
        if (index >= 0 && index < this.items.length) this.activateItem(index, { scrollIntoView: false });
      });
    }

    onHorizontalSliderIndexChange(index) {
      this.activateItem(index, { scrollIntoView: false });
    }

    alignMarkersToHeaders() {
      if (!this.items?.length) return;
      this.items.forEach((item) => {
        const point = item.querySelector('.timeline__point');
        const line = item.querySelector('.timeline__line');
        if (point) point.style.marginTop = '';
        if (line) line.style.marginBottom = '';
      });
      for (let i = this.items.length - 1; i >= 0; i--) {
        const item = this.items[i];
        const header = item.querySelector('.timeline-item__header');
        const point = item.querySelector('.timeline__point');
        if (!header || !point) continue;
        const offset = Math.max(0, Math.round(((header.offsetHeight || 0) - (point.offsetHeight || 0)) / 2));
        if (offset > 0) point.style.marginTop = `${offset}px`;
        if (i - 1 < 0) continue;
        const previousLine = this.items[i - 1].querySelector('.timeline__line');
        if (!previousLine) continue;
        previousLine.style.marginBottom =
          offset > 0 ? `calc((var(--items-spacing-vertical) - var(--line-point-spacing)) * -1 - ${offset}px)` : '';
      }
    }

    disconnectedCallback() {
      if (typeof this.detachScroll === 'function') this.detachScroll();
      if (this._restoreTimer) {
        clearTimeout(this._restoreTimer);
        this._restoreTimer = null;
      }
    }
  }
  customElements.define('shrine-timeline', ShrineTimeline);

  class TimelineSlider extends SliderComponent {
    constructor() {
      super();
      this.timelineOwner = this.closest('shrine-timeline') || null;
      this.currentIndex = 0;
    }

    get maxIndex() {
      return Math.max(0, (this.sliderItemsToShow?.length || 0) - 1);
    }

    clampIndex(index) {
      return Math.max(0, Math.min(index, this.maxIndex));
    }

    get isTimelineClickMode() {
      const owner = this.timelineOwner;
      if (!owner) return false;
      return (owner.styleHorizontal || owner.dataset.styleHorizontal) === 'slider_click';
    }

    scrollToCenterIndex(index) {
      if (!this.slider || !this.sliderItemOffset) return;
      const perPage = this.slidesPerPage || 1;
      const maxStart = Math.max(0, this.sliderItemsToShow.length - perPage);
      const start = Math.min(Math.max(index - Math.floor(perPage / 2), 0), maxStart);
      this.slider.scrollTo({ left: start * this.sliderItemOffset * this.scrollMultiplier, behavior: 'smooth' });
    }

    updateHorizontalArrows() {
      if (!this.prevButton || !this.nextButton) return;
      this.toggleDisabled(this.prevButton, this.currentIndex <= 0);
      this.toggleDisabled(this.nextButton, this.currentIndex >= this.maxIndex);
    }

    goToIndex(index) {
      if (!this.isTimelineClickMode) return;
      const next = this.clampIndex(index);
      if (next === this.currentIndex) return;
      this.currentIndex = next;
      this.scrollToCenterIndex(this.currentIndex);
      this.updateHorizontalArrows();
      this.timelineOwner?.onHorizontalSliderIndexChange?.(this.currentIndex);
    }

    onButtonClick(event) {
      if (!this.isTimelineClickMode) return super.onButtonClick(event);
      event.preventDefault();
      this.goToIndex(this.currentIndex + (event.currentTarget.name === 'next' ? 1 : -1));
    }

    setCurrentIndexFromTimeline(index) {
      if (this.isTimelineClickMode) this.goToIndex(index);
    }

    update() {
      if (!this.isTimelineClickMode) return super.update();
      this.updateHorizontalArrows();
    }
  }
  customElements.define('timeline-slider', TimelineSlider);
})();
